/**
 * Cut Action
 * Marks files/folders for moving (cut operation)
 * The pending cut lives in a zustand store, not on `window`.
 */

import { create } from "zustand";

import { Datum } from "../datastructure";
import { ActionDefinition, ActionContext } from "./types";
import { isReadOnlyDatum } from "../specialFolders";

export interface CutItem {
  datumId: string;
}

type CutChangeListener = (items: CutItem[]) => void;

interface CutClipboardStore {
  /** The items waiting to be pasted; empty when nothing is cut. */
  items: CutItem[];
  setItems: (items: CutItem[]) => void;
  clear: () => void;
}

/**
 * Page-scoped clipboard for cut operations: a cut survives the unmount of the
 * menu that made it and is pasted from a different subtree, so it cannot be
 * component state. The store is module-level by design — the storybook harness
 * aliases `zustand` and resets every tracked store before each story.
 */
export const useCutClipboardStore = create<CutClipboardStore>((set) => ({
  items: [],
  setItems: (items: CutItem[]) => set({ items }),
  clear: () => set({ items: [] }),
}));

/**
 * Subscribe to cut item changes.
 * Returns an unsubscribe function.
 */
export function onCutItemsChange(listener: CutChangeListener): () => void {
  return useCutClipboardStore.subscribe((state) => listener(state.items));
}

/**
 * Get cut items from clipboard
 */
export function getCutItems(): CutItem[] {
  return useCutClipboardStore.getState().items;
}

/**
 * Set cut items to clipboard
 */
export function setCutItems(items: CutItem[]): void {
  useCutClipboardStore.getState().setItems(items);
}

/**
 * Clear cut items from clipboard
 */
export function clearCutItems(): void {
  useCutClipboardStore.getState().clear();
}

/**
 * Check if a datum is in the cut clipboard
 */
export function isDatumCut(datumId: string): boolean {
  return getCutItems().some((item) => item.datumId === datumId);
}

/**
 * Cut a single datum (file or folder)
 */
export async function cutDatum(
  datum: Datum,
  context: ActionContext
): Promise<void> {
  setCutItems([{ datumId: datum.id }]);
  context.showToast?.(`Cut "${datum.name}"`, "info");
}

/**
 * Action definition for context menu
 */
export const cutAction: ActionDefinition = {
  config: {
    action: "cut",
    label: "Cut",
  },
  // Cut is only meaningful where the matching Paste can complete, and Paste
  // resolves its items through the live tree — see pasteAction below.
  isApplicable: (datum, context) =>
    Boolean(context?.filetree) &&
    datum.path.length > 0 &&
    !isReadOnlyDatum(datum, context),
  execute: async (datum, context) => {
    await cutDatum(datum, context);
  },
};
